// 会话control
import log from "../log";
import Context from "./context";
import { Topic, topic_upsert, topic_query_normal, topic_query_top, topic_query_one, topic_modify, topic_del } from "../db/t_topic";
import { config_get, config_set } from "../db/t_config";
import time from "../time";
import { Msg, msg_count, msg_query_last_mid, msg_query_latest_one } from "../db/t_msg";
import { UserobjController } from "./userobj";
import UserController from "./user";
import MsgController from "./msg";
import { GroupController } from "./group";
import { group_summary_del, group_summary_upsert, GroupSummary } from "../db/t_group_summary";
import { User } from "../db/t_user";
import { GroupMember } from "../db/t_group_member";
import { Group } from "../db/t_group";
import globalEventBus from "../../src/utils/EventBus";
import { FullProcessData } from "./mq";
import { last } from "lodash";
import { MessageType } from "../../src/constants/types";

// 会话类型定义
export enum TopicTag {
    UNKNOWN = 0, // 0 -不知道的未分配
    ENTERPRISE = 1, // 1-企业群
    SUPPLIER = 2, // 2-供应商
    SERVICES = 3, // 3-服务商
    CHAIN = 4, // 4-供应链
}

export type TopicProps = {
    tid: string; // 会话Id
    hidemid?: string; // 设置隐藏时会话的消息id，后面收到更大的消息ID，则会话就又不隐藏
    order?: number; // 排序，越大优先级越高，初始0，用途：置顶/取消置顶,取的就是置顶操作时的系统时间 毫秒级别
    lvTime?: number; // 上次打开时间,用途：同order级别按visitTime时间排序
    lsMid?: string; // 展示了的 最大消息id，用途：会话计数
    remark?: string; // 备注
    mute?: boolean; // 是否静音
};

export type Avatar = {
    name: string;
    uid: number;
    avatar?: string;
};

// 会话摘要信息
export type TopicSummary = {
    latest?: Msg;
    latestAt?: Msg;
    latestVisibleMsg?: Msg; //最后一条正常的消息 visible 为 0,用来区分敏感词
    unreadCnt?: number;
    draft?: any; // todo 补充草稿
    avatarList: Avatar; // 所有的群组用户的头像
    tag: TopicTag;
    scene: number;
    sceneid: string;
    company: string;
    isQuit: boolean; // 是否已经退出；群聊就是退群了
};

export type TopicCreateBody = {
    name: string; // 群聊的名称，在群聊有效，单聊不用
    members: number[]; // type为group时是群成员；type为single时是单聊，是对方和自己的uid
    type: "single" | "group";
    intro?: string; // 群简介，在群聊有效，单聊不用
};

export type TopicWithSummary = { topic: Topic; summary?: TopicSummary; group?: Group; users?: (User | GroupMember)[]; hasApply?: boolean };

export default class TopicController {
    private static lastRefreshTime: number = 0;
    private static isRefreshing: boolean = false;
    private static emptyGroup: { [key: string]: boolean } = {};
    private static emptyTopicMsg: { [key: string]: boolean } = {}; // 这个会话的确没有消息

    public static Clear() {
        TopicController.lastRefreshTime = 0;
        TopicController.isRefreshing = false;
        TopicController.emptyGroup = {};
        TopicController.emptyTopicMsg = {};
    }
    private db_key = "topic_fresh_time";
    // 创建会话
    public async Create(ctx: Context, data: TopicCreateBody): Promise<{ error: any; topic?: Topic }> {
        const r = await ctx.conn.Call("/v1/sdk/topic/create", {
            name: data.name,
            members: data.members,
            type: data.type,
            intro: data.intro,
        });
        if (!(r && r.code === 0)) {
            log.debug("创建会话失败", JSON.stringify(r));
            return { error: r };
        }
        if (!(r.data && r.data.topic)) {
            log.debug("创建会话失败,EmptyTopicResponse", JSON.stringify(r));
            return { error: "BUG!!!EmptyTopicResponse" };
        }
        const topic = r.data.topic as Topic;
        const r1 = await topic_upsert(ctx.db, topic);
        await this.upsetGroup(ctx, topic);
        ctx.mq.emit("topic_new", { topic_new: { tidList: [topic.tid] } });
        if (r1.error) {
            return { error: r1.error };
        }
        return { error: null, topic: topic };
    }

    private async upsetGroup(ctx: Context, topic: Topic) {
        if (topic.type === "group") {
            const groupSummary: GroupSummary = {
                tid: topic.tid,
                uid: topic.uid,
                ctime: topic.ctime,
                lutime: topic.ctime,
                lctime: topic.ctime,
                tag: "",
                tagid: 1,
                remark: "",
                avatar: "",
                name: topic.name,
                namePY: "",
                namePYS: "",
            };
            await group_summary_upsert(ctx.db, groupSummary);
        }
    }

    // 先查数据库，如果判断更新时间是否大于了20分钟 是的话触发一次更新，更新后事件回调
    public async Detail(
        ctx: Context,
        tid: string,
        sync: boolean = false,
        isEmit: boolean = true
    ): Promise<{ error: any; topic?: TopicWithSummary }> {
        const r = await topic_query_one(ctx.db, tid);
        if (r.error) {
            return { error: r.error };
        }
        const exp = time.toUnixMillSecond(time.add(time.now(), -2 * time.HOUR));
        if (!r.topic || (r.topic && r.topic.lutime <= exp)) {
            if (sync || !TopicController.emptyGroup[tid]) {
                if (sync) {
                    const r1 = await this.DetailRemote(ctx, tid, isEmit);
                    if (r1.error) {
                        return { error: r1.error };
                    }
                    r.topic = r1.topic;
                } else {
                    this.DetailRemote(ctx, tid, isEmit);
                }
            }
        }

        if (!r.topic) {
            return { error: null };
        }
        const r1 = await this.fix_topic_summary(ctx, [r.topic], sync);
        if (r1.topics && r1.topics.length > 0) {
            return {
                error: null,
                topic: r1.topics[0],
            };
        }
        return {
            error: null,
            topic: { topic: r.topic },
        };
    }

    // 从远程获取
    public async DetailRemote(ctx: Context, tid: string, isEmit: boolean = true): Promise<{ error: any; topic?: Topic }> {
        const res = await ctx.conn.Call("/v1/sdk/topic/detail", { tidList: [tid] });
        if (!(res && res.code === 0)) {
            return { error: JSON.stringify(res) };
        }
        if (!(res.data && res.data.topics && res.data.topics.length > 0)) {
            log.info("GetTopicDetail", tid, "Remote Empty");
            TopicController.emptyGroup[tid] = true;
            return { error: null };
        }
        delete TopicController.emptyGroup[tid];
        const dd = res.data.topics as Topic[];
        const topic = dd[0];
        if (topic.type === "group") {
            const r1 = await ctx.conn.Call("/v1/sdk/group/detail", { tid: tid });
            if (r1.data.group && r1.data.group.flag === 2) {
                return { error: null };
            }
        }
        await topic_upsert(ctx.db, topic);
        await this.upsetGroup(ctx, topic);
        if (isEmit) {
            ctx.mq.emit("topic_new", {
                topic_new: { tidList: [topic.tid] },
            });
        }
        return { error: null, topic: topic };
    }

    //设置草稿
    public async SetDraft(ctx: Context, tid: string, draft: string) {
        const r = await topic_modify(ctx.db, { tid: tid, draft: draft });
        if (r.rowsAffected > 0) {
            ctx.mq.emit("topic_update_draft", {
                topic_update_draft: { tid: tid, draft },
            });
        }
        return r;
    }

    public static async DelTopic(ctx: Context, tid: string): Promise<{ code: any }> {
        const r = await topic_del(ctx.db, tid);
        if (tid.startsWith("g_")) {
            const r1 = await group_summary_del(ctx.db, tid);
        }
        if (r.rowsAffected > 0) {
            return { code: 0 };
        }
        return { code: -1 };
    }

    // 打开了会话，当最大的消息刷新的时候更新
    // 是否静音等等、是否置顶等等
    public async SetProps(ctx: Context, props: TopicProps): Promise<{ error: any }> {
        let errList: any[] = [];
        const userobjController = new UserobjController();
        // 修改访问的最大Id
        if (props.lsMid) {
            const err = await userobjController.Set(ctx, {
                resId: props.tid,
                resType: "topic_visit",
                data: props.lsMid,
            });
            err.error && errList.push(err.error);
            // 本地更新 ls_mid；同时 unread=0。否则 msg_synced≠1 时 get_summary 仍用列上 unread，刷新后会话列表角标与 GetTopicDetail 一直偏大
            await topic_modify(ctx.db, { tid: props.tid, lsMid: props.lsMid, unread: 0 });
            const utime = time.toUnixMillSecond(time.now());
            ctx.mq.emit("topic_visit", {
                topic_visit: {
                    tid: props.tid,
                    maxshowmid: props.lsMid,
                    utime: utime,
                },
            });
        }

        if (props.remark) {
            const err = await userobjController.Set(ctx, {
                resId: props.tid,
                resType: "topic_remark",
                data: props.remark,
            });
            err.error && errList.push(err.error);
        }

        if (props.hidemid) {
            const err = await userobjController.Set(ctx, {
                resId: props.tid,
                resType: "topic_delete",
                data: props.hidemid,
            });
            err.error && errList.push(err.error);
        }

        if (props.mute !== undefined) {
            const err = await userobjController.Set(ctx, {
                resId: props.tid,
                resType: "topic_mute",
                data: props.mute ? "true" : "false",
            });
            err.error && errList.push(err.error);
        }

        if (props.order !== undefined && props.order >= 0) {
            const err = await userobjController.Set(ctx, {
                resId: props.tid,
                resType: "topic_to_top",
                data: props.order > 0 ? "true" : "false",
            });
            err.error && errList.push(err.error);
        }

        return { error: errList.length > 0 ? errList : null };
    }

    // 获取会话的摘要信息 最近的消息 最近未读消息里at自己的消息 草稿(单独一张表)
    private async get_summary(
        ctx: Context,
        topic: Topic,
        sync: boolean = false
    ): Promise<{
        error: any;
        summary?: TopicSummary;
        group?: Group;
        users?: (User | GroupMember)[];
    }> {
        // 1. 获得最近的消息 非删除的
        let latest = await msg_query_latest_one(ctx.db, topic.tid, { mask: 1, unequalMask: true });
        let latestVisibleMsg = latest
        if (latest.error) {
            latestVisibleMsg.error = undefined;
            latestVisibleMsg.msg = undefined;
            console.error("获取会话最新消息失败", topic.tid, JSON.stringify(latest.error));
            // 不能返回,因为后面还有很多数据逻辑
            // return { error: latest.error };
        }

        // let latestVisibleMsg = latest;
        if (!latest.msg && !TopicController.emptyTopicMsg[topic.tid]) {
            if (sync) {
                const res = await MsgController.QryLatest(ctx, topic.tid);
                if (res.msgs && res.msgs.length > 0) {
                    latest.msg = res.msgs[0];
                    const filter = res.msgs.filter((v) => v.mid.length > 0 && v.visible === 0);
                    if (filter.length > 0) {
                        latestVisibleMsg.msg = filter[0];
                    }
                } else {
                    log.info(topic.tid, "强制获取最近消息", 0, "条");
                    TopicController.emptyTopicMsg[topic.tid] = true;
                }
            } else {
                // 一条消息都没有，去查一下最近的50条消息,查完通知外部更新
                MsgController.QryLatest(ctx, topic.tid).then((res) => {
                    if (res.error) {
                        log.info("空会话强制获取最新消息失败", topic.tid, JSON.stringify(res.error));
                        return;
                    }
                    if (res.msgs && res.msgs.length > 0) {
                        log.info(topic.tid, "强制获取最近消息", res.msgs.length, "条");
                        ctx.mq.emit("msg_new", { msg_new: { tid: topic.tid, uid: topic.uid } });
                    } else {
                        log.info(topic.tid, "强制获取最近消息", 0, "条");
                        TopicController.emptyTopicMsg[topic.tid] = true;
                    }
                });
            }
        }

        // —— 未读与 ls_mid 关系（列表角标 summary.unreadCnt 由此算出）——
        // 1) t_topic.ls_mid：已读游标，仅由 TopicController.SetProps(客户端进会话)、sync 下行 topic_visit 写入；get_summary 绝不回写 ls_mid。
        // 2) countMinMid：只用于 msg_count / @ 查询的 minMid；有 ls_mid 用行上值，否则临时用「库里最后一条消息 mid」参与计数（仍不写回 ls_mid）。
        // 3) 若错误地把「最后一条 mid」写进 ls_mid，会导致 ChatList reportLatestReader2 里 mid>lsMid 永远不成立，SetTopicProps/topic_visit 无法上报。
        // 4) msg_synced≠1 时未读用列 t_topic.unread（服务端/增量），否则用 msg_count(minMid=countMinMid)。
        let countMinMid = topic.lsMid ?? "";
        if (countMinMid.length === 0) {
            const { mid } = await msg_query_last_mid(ctx.db, topic.tid);
            if (mid && mid.length > 0) {
                countMinMid = mid;
            }
        }

        // 2. 获得会话已读最大的消息id
        // 3. 未读消息中 查询消息字段中有没有at自己
        const latestAt = await msg_query_latest_one(ctx.db, topic.tid, {
            atme: true,
            minMid: countMinMid,
            mask: 0,
        });

        let latestAtMsg
        if (latestAt.error) {
            latestAtMsg = undefined;
            console.error("获取会话@自己的消息失败", topic.tid, JSON.stringify(latestAt.error));
            // 不能返回,因为后面还有很多数据逻辑
            // return { error: latestAt.error };
        } else {
            latestAtMsg = latestAt.msg;
        }

        if (!(latestVisibleMsg && latestVisibleMsg.msg && latestVisibleMsg.msg.mid.length > 0 && latestVisibleMsg.msg.visible === 0)) {
            latestVisibleMsg = await msg_query_latest_one(ctx.db, topic.tid, {
                mask: 0,
                isVisible: true,
            });
        }
        // 5. 获得会话的未读数量

        const unreadCnt = await msg_count(ctx.db, topic.tid, {
            expectSender: ctx.uid, // 未读统计，排出发送用户是自己的， 因为自己发送的消息不用当作未读
            minMid: countMinMid,
            mask: 0,
        });
        // 4. 查询草稿表有没有草稿数据 todo

        let unreadCount = 0;
        if (unreadCnt.error) {
            // 不能返回,因为后面还有很多数据逻辑
            console.error("获取会话未读数量失败", topic.tid, JSON.stringify(unreadCnt.error));
            // return { error: unreadCnt.error };
        } else {
            unreadCount = unreadCnt.count || 0;
        }
        // 未拉齐历史（全量只有 lastMsg 等）：列表未读以 t_topic.unread 为准（/sync/topic 写入 + 增量 sync 累加）
        // 已拉齐后改回按本地消息表 + ls_mid 统计
        const msgSynced = (topic.msg_synced ?? 0) === 1;
        if (!msgSynced) {
            unreadCount = Math.max(0, topic.unread || 0);
        }

        // 5. 获取头像信息
        // 查用户信息 查群信息 查到后更新到本地 然后通知会话变更
        let tag: TopicTag = TopicTag.UNKNOWN;
        let avatars: Avatar = { name: "", uid: -1 };
        let isQuit = false;
        let scene: number = 0;
        let sceneid: string = "";
        let company: string = "";
        let topicUsers: (User | GroupMember)[] = [];
        let group: Group | undefined;
        if (topic.type === "group") {
            // 仅从 DB 读取群信息+成员，不触发远程（群成员改为进入聊天时按需加载）
            const r = await GroupController.QryLocal(ctx, topic.tid);
            if (r.error) {
                log.error("获取群详情信息失败", JSON.stringify(r.error));
            }
            group = r.group;
            if (r.members && r.members.length > 0) {
                topicUsers = r.members;
            }
            if (r.group?.name) {
                topic.name = r.group.name;
            }

            tag = r.group?.tagid || TopicTag.UNKNOWN;
            if (r.group?.avatar) {
                avatars = { name: "", avatar: r.group.avatar, uid:topic.uid };
            }

            // 批量全量往往只写入 lastMsg 对应的一条拼凑成员；其它成员发消息后若尚未落库，
            // 会话列表从 QryLocal 拿不到 sender。用「列表展示用的最后一条可见消息」的 meta 再拼一条
            // 成员并入 users（与 full_sync.syncTopic 一致）。
            const vis = latestVisibleMsg?.msg;
            const listMsg =
                vis &&
                vis.mid &&
                vis.mid.length > 0 &&
                vis.visible === 0
                    ? vis
                    : latest.msg;
            if (
                listMsg &&
                listMsg.sender &&
                listMsg.type !== MessageType.SYSTEM
            ) {
                const exists = topicUsers.some(
                    (u) => "uid" in u && (u as GroupMember).uid === listMsg.sender
                );
                if (!exists) {
                    const meta: any = listMsg.meta || {};
                    const senderNick = meta.senderNick || "";
                    const senderName = meta.senderName || "";
                    if (senderNick || senderName) {
                        topicUsers = [
                            ...topicUsers,
                            {
                                tid: topic.tid,
                                uid: listMsg.sender,
                                nick: senderNick,
                                name: senderName,
                                namePY: "",
                                namePYS: "",
                                nickPY: "",
                                nickPYS: "",
                                sex: "",
                                birth: "",
                                privacy: "",
                                telno: "",
                                email: "",
                                appkey: "",
                                mainAppkey: "",
                                avatar: "",
                                remark: "",
                                status: "",
                                lutime: 0,
                                lctime: 0,
                            },
                        ];
                    }
                }
            }
        } else {
            const r = await UserController.Get(ctx, topic.uid);
            if (r.user) {
                topicUsers.push(r.user);
                const name = r.user.remark || r.user.nick || r.user.name || "";
                topic.name = name;
                avatars = { name: name, avatar: r.user.avatar, uid: topic.uid };
            }
        }
        return {
            error: null,
            summary: {
                latest: latest.msg,
                latestAt: latestAtMsg,
                latestVisibleMsg: latestVisibleMsg.msg,
                unreadCnt: unreadCount,
                avatarList: avatars,
                tag: tag,
                scene: scene,
                sceneid: sceneid,
                company: company,
                isQuit: isQuit,
            },
            group: group,
            users: topicUsers,
        };
    }

    // 优先从本地数据库返回结果
    public async GetList(
        ctx: Context,
        type: "top" | "normal" // top - 置顶 normal - 非置顶的其它
    ): Promise<{ error: any; topics?: TopicWithSummary[] }> {
        let r1: { error: any; topics?: Topic[] };
        if (type === "top") {
            r1 = await topic_query_top(ctx.db); // 置顶的会话列表
        } else {
            r1 = await topic_query_normal(ctx.db, 0); // 非置顶的其它会话列表（0表示不限制数量）
        }

        if (r1.error) {
            return { error: r1.error };
        }
        if (!(r1.topics && r1.topics.length > 0)) {
            this.check_or_update(ctx).then(() => {});
            return { error: null };
        }
        const r2 = await this.fix_topic_summary(ctx, r1.topics, true);
        this.check_or_update(ctx).then(() => {});
        return r2;
    }

    // 优先从本地数据库返回结果
    public async GetAllListRemote(ctx: Context): Promise<void> {
        globalEventBus.emit("on_refresh", { "name": "start" })
        const remote = await this.GetTopicListRemote(ctx, true, 20);
        const top = remote.data?.topTopics;
        const normal = remote.data?.topics;
        if (top && top.length > 0) {
            await this.fix_topic_summary(ctx, top, true, true).then();
        }
        if (normal && normal.length > 0) {
            await this.fix_topic_summary(ctx, normal, true, true).then();
        }
        globalEventBus.emit("on_refresh", { "name": "done" })
        return;
    }

    // 优先从本地数据库返回结果
    public async GetAllList(
        ctx: Context,
        sync: boolean = false, // 本地没有是否同步到远端去查
        isEmit: boolean = false
    ): Promise<{ isAllRefresh: boolean; topTopics?: TopicWithSummary[]; normalTopics?: TopicWithSummary[] }> {
        let topTopics;
        let normalTopics;
        let isAllRefresh = true;
        const top = await topic_query_top(ctx.db); // 置顶的会话列表
        const normal = await topic_query_normal(ctx.db, 0); // 非置顶的其它会话列表（0表示不限制数量）
        const topIsEmpty = !(top.topics && top.topics.length > 0);
        const normalIsEmpty = !(normal.topics && normal.topics.length > 0);

        log.info("[TopicController] GetAllList sync=", sync, "isEmit=", isEmit,
            " top.count=", top.topics?.length ?? 0, "normal.count=", normal.topics?.length ?? 0,
            " topIsEmpty=", topIsEmpty, "normalIsEmpty=", normalIsEmpty);

        if (sync && topIsEmpty && normalIsEmpty) {
            const remote = await this.GetTopicListRemote(ctx, sync);
            top.topics = remote.data?.topTopics;
            normal.topics = remote.data?.topics;
            isAllRefresh = false;
            log.info("[TopicController] GetAllList 远程返回 top.count=", top.topics?.length ?? 0, "normal.count=", normal.topics?.length ?? 0);
        } else {
            this.check_or_update(ctx)
        }
        if (top.topics && top.topics.length > 0) {
            const r2 = await this.fix_topic_summary(ctx, top.topics, sync, !isAllRefresh || isEmit);
            topTopics = r2.topics;
        }
        if (normal.topics && normal.topics.length > 0) {
            const r2 = await this.fix_topic_summary(ctx, normal.topics, sync, !isAllRefresh || isEmit);
            normalTopics = r2.topics;
            log.info("[TopicController] GetAllList fix_topic_summary 完成 normal.count=", r2.topics?.length ?? 0);
        }

        return { isAllRefresh, topTopics, normalTopics };
    }

    // 共享的代码段，补充topic的摘要信息
    private async fix_topic_summary(
        ctx: Context,
        topics: Topic[],
        sync: boolean = false,
        isEmit: boolean = false
    ): Promise<{ error: any; topics?: TopicWithSummary[] }> {
        let err: any = null;
        const rst: TopicWithSummary[] = [];
        for (const topic of topics) {
            const s = await this.get_summary(ctx, topic, sync);
            if (s.error) {
                err = s.error;
            }
            const topicDetail = { topic: topic, summary: s.summary, users: s.users, group: s.group };
            if (isEmit) {
                ctx.mq.emit("topic_qry", {
                    topic_qry: { topicDetail },
                });
            }
            rst.push(topicDetail);
        }
        return { error: err, topics: rst };
    }

    // 全量更新, 然后检查下最后一次全量查询的时间，超过了1小时就全量查询一把
    // 注意：现在全量同步由 FullSyncRunner 负责，这里不再主动触发远程拉取
    // 只做本地 DB 读取即可，增量 /sync 和全量 sync 都会把数据写入 DB
    private async check_or_update(ctx: Context) {
        // 全量同步机制已由 FullSyncRunner 在 SyncRunable 中接管
        // 这里不再触发 GetTopicListRemote 远程拉取
        return;
    }

    private async GetTopicListRemote(
        ctx: Context,
        sync: boolean = false,
        size: number = 100
    ): Promise<{ error: any; data?: { topTopics: Topic[]; topics: Topic[] } }> {
        const rsp = await ctx.conn.Call("/v1/sdk/topic/list", {
            skip: 0,
            size: size,
            order: 1,
        });
        if (!(rsp && rsp.code === 0)) {
            log.warn("查询topic列表失败,稍后再查");
            TopicController.isRefreshing = false;
            return { error: rsp.err };
        }

        if (rsp && rsp.data && rsp.data.topTopics) {
            const topics = rsp.data.topTopics as Topic[];
            const n = topics.length;
            for (let i = 0; i < n; i++) {
                await topic_upsert(ctx.db, topics[i]);
            }
        }

        if (rsp && rsp.data && rsp.data.topics) {
            const topics = rsp.data.topics as Topic[];
            const n = topics.length;
            for (let i = 0; i < n; i++) {
                await topic_upsert(ctx.db, topics[i]);
            }
        }
        TopicController.lastRefreshTime = time.toUnixMillSecond(time.now());
        await config_set(ctx.db, this.db_key, TopicController.lastRefreshTime.toString(10));
        // 通知会话刷新了
        log.info("====同步会话完成");
        if (sync) {
            return rsp;
        }
        ctx.mq.emit("topic_refresh", {});
        return { error: null };
    }
}
